import { useEffect, useRef, useState } from 'react'
import { api, ApiError, uploadFile } from './api'
import { FileAudioIcon, FileVideoIcon, FolderIcon } from './icons'
import { Modal, Spinner, toast } from './ui'
import { cls } from './util'
import { etaText, HowItWorks } from './project/Processing'

type Entry = { path: string; name: string; dir: boolean; size: number; kind: string; modified: string }

const gb = (n: number) => (n >= 1e9 ? `${(n / 1e9).toFixed(1)} GB` : n >= 1e6 ? `${Math.round(n / 1e6)} MB` : `${Math.round(n / 1e3)} KB`)

export function ImportDialog({ projectId, onClose, onDone }: { projectId: string; onClose: () => void; onDone?: () => void }) {
  const [tab, setTab] = useState<'cloud' | 'upload'>('upload')
  return (
    <Modal title="Add footage" onClose={onClose} wide>
      <HowItWorks />
      <div className="tabs">
        <button className={cls('tab', tab === 'upload' && 'on')} onClick={() => setTab('upload')}>Upload from your computer</button>
        <button className={cls('tab', tab === 'cloud' && 'on')} onClick={() => setTab('cloud')}>Already on Osmo Cloud</button>
      </div>
      {tab === 'cloud' ? <CloudPicker projectId={projectId} onDone={() => { onDone?.(); onClose() }} /> : <Uploader projectId={projectId} onDone={() => { onDone?.(); onClose() }} />}
    </Modal>
  )
}

function CloudPicker({ projectId, onDone }: { projectId: string; onDone: () => void }) {
  const [path, setPath] = useState('')
  const [entries, setEntries] = useState<Entry[] | null>(null)
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const [picked, setPicked] = useState<Map<string, Entry>>(new Map())
  useEffect(() => {
    setEntries(null)
    setErr('')
    api.get<{ entries: Entry[] }>(`cloud/list?path=${encodeURIComponent(path)}`).then((r) => setEntries(r.entries)).catch((e) => setErr(e.message))
  }, [path])
  const crumbs = path ? path.split('/') : []
  const media = entries?.filter((e) => e.kind === 'video' || e.kind === 'audio') || []
  const toggle = (e: Entry) =>
    setPicked((cur) => {
      const next = new Map(cur)
      if (next.has(e.path)) next.delete(e.path)
      else next.set(e.path, e)
      return next
    })
  const allHere = media.length > 0 && media.every((e) => picked.has(e.path))
  const toggleAll = () =>
    setPicked((cur) => {
      const next = new Map(cur)
      for (const e of media) allHere ? next.delete(e.path) : next.set(e.path, e)
      return next
    })
  const clips = [...picked.values()].filter((e) => !e.dir)
  const folders = [...picked.values()].filter((e) => e.dir)
  const pickedSize = clips.reduce((n, e) => n + e.size, 0)
  const doImport = async (paths: string[]) => {
    setBusy(true)
    try {
      const r = await api.post<{ added: number; found: number }>(`projects/${projectId}/import`, { paths })
      toast(r.added ? `Added ${r.added} file${r.added > 1 ? 's' : ''}. Lining up cameras and sound now.` : 'Those were already in the project.')
      onDone()
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Import failed', 'err')
      setBusy(false)
    }
  }
  return (
    <div className="picker">
      <div className="crumbs">
        <button onClick={() => setPath('')}>Osmo</button>
        {crumbs.map((c, i) => (
          <span key={i}>
            <span className="sep">/</span>
            <button onClick={() => setPath(crumbs.slice(0, i + 1).join('/'))}>{c}</button>
          </span>
        ))}
        <div className="grow" />
        {media.length > 0 && (
          <label className="toggle small">
            <input type="checkbox" checked={allHere} onChange={toggleAll} /> All {media.length} clips here
          </label>
        )}
      </div>
      <div className="picker-list">
        {err && <p className="error pad">{err}</p>}
        {!entries && !err && <div className="center-pad"><Spinner /></div>}
        {entries?.length === 0 && <p className="muted pad">Empty folder.</p>}
        {entries?.map((e) =>
          e.dir ? (
            <div key={e.path} className={cls('pick-row dir', picked.has(e.path) && 'on')}>
              <input type="checkbox" checked={picked.has(e.path)} onChange={() => toggle(e)} aria-label={`Pick folder ${e.name}`} title="Pick the whole folder" />
              <button className="pick-open" onClick={() => setPath(e.path)}>
                <span className="pick-ico"><FolderIcon /></span>
                <span className="grow ellipsis">{e.name}</span>
                <span className="muted small">{e.size ? gb(e.size) : ''}</span>
              </button>
            </div>
          ) : e.kind === 'other' ? (
            <div key={e.path} className="pick-row dim">
              <span className="pick-ico" />
              <span className="grow ellipsis">{e.name}</span>
              <span className="muted small">{gb(e.size)}</span>
            </div>
          ) : (
            <label key={e.path} className={cls('pick-row file', picked.has(e.path) && 'on')}>
              <input type="checkbox" checked={picked.has(e.path)} onChange={() => toggle(e)} />
              <span className="pick-ico">{e.kind === 'video' ? <FileVideoIcon /> : <FileAudioIcon />}</span>
              <span className="grow ellipsis">{e.name}</span>
              <span className="muted small">{gb(e.size)}</span>
            </label>
          ),
        )}
      </div>
      <div className="form-actions">
        <span className="muted small grow">
          {picked.size
            ? `${clips.length ? `${clips.length} clip${clips.length > 1 ? 's' : ''} (${gb(pickedSize)})` : ''}${clips.length && folders.length ? ' + ' : ''}${folders.length ? `${folders.length} folder${folders.length > 1 ? 's' : ''}` : ''} picked. We line up the cameras and sound for you.`
            : 'Tick the clips you want (any folders), or import everything in this folder.'}
        </span>
        {picked.size > 0 && <button className="btn btn-ghost" onClick={() => setPicked(new Map())}>Clear</button>}
        {picked.size > 0 ? (
          <button className="btn btn-primary" disabled={busy} onClick={() => doImport([...picked.keys()])}>{busy ? 'Importing…' : 'Import picked'}</button>
        ) : (
          <button className="btn btn-primary" disabled={!path || busy || !entries?.length} onClick={() => doImport([path])}>{busy ? 'Importing…' : 'Import whole folder'}</button>
        )}
      </div>
    </div>
  )
}

type Up = { file: File; done: number; state: 'waiting' | 'uploading' | 'done' | 'error'; error?: string }

function Uploader({ projectId, onDone }: { projectId: string; onDone: () => void }) {
  const [files, setFiles] = useState<Up[]>([])
  const [running, setRunning] = useState(false)
  const [over, setOver] = useState(false)
  const input = useRef<HTMLInputElement>(null)
  const abort = useRef<AbortController>()
  const add = (list: FileList | null) => {
    if (!list) return
    const ok = [...list].filter((f) => /\.(mov|mp4|m4v|mxf|wav|aif|aiff|mp3|m4a)$/i.test(f.name))
    if (ok.length < list.length) toast('Skipped files that are not video or audio.', 'err')
    setFiles((cur) => [...cur, ...ok.map((file) => ({ file, done: 0, state: 'waiting' as const }))])
  }
  const start = async () => {
    setRunning(true)
    abort.current = new AbortController()
    for (let i = 0; i < files.length; i++) {
      if (files[i].state === 'done') continue
      setFiles((cur) => cur.map((f, j) => (j === i ? { ...f, state: 'uploading' } : f)))
      try {
        await uploadFile(projectId, files[i].file, (done) => setFiles((cur) => cur.map((f, j) => (j === i ? { ...f, done } : f))), abort.current.signal)
        setFiles((cur) => cur.map((f, j) => (j === i ? { ...f, state: 'done', done: f.file.size } : f)))
      } catch (e) {
        setFiles((cur) => cur.map((f, j) => (j === i ? { ...f, state: 'error', error: e instanceof Error ? e.message : 'failed' } : f)))
        if (abort.current.signal.aborted) break
      }
    }
    setRunning(false)
  }
  const total = files.reduce((n, f) => n + f.file.size, 0)
  const done = files.reduce((n, f) => n + f.done, 0)
  const allDone = files.length > 0 && files.every((f) => f.state === 'done')
  // Time left from the speed so far.
  const t0 = useRef(0)
  if (running && !t0.current) t0.current = Date.now()
  if (!running) t0.current = 0
  const el = t0.current ? (Date.now() - t0.current) / 1000 : 0
  const eta = running && done > 0 && el > 3 ? etaText(((total - done) / done) * el) : ''
  return (
    <div className="uploader">
      <div
        className={cls('drop', over && 'over')}
        onDragOver={(e) => { e.preventDefault(); setOver(true) }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); add(e.dataTransfer.files) }}
        onClick={() => input.current?.click()}
      >
        <input ref={input} type="file" multiple hidden accept="video/*,audio/*,.mxf,.mov,.wav" onChange={(e) => add(e.target.files)} />
        <b>Drop video or audio here</b>
        <span className="muted small">Every camera's files plus any sound recorder files. They're saved to this project's folder on Osmo Cloud, then lined up and transcribed. Keep this tab open while it uploads.</span>
      </div>
      {files.length > 0 && (
        <div className="up-list">
          {files.map((f, i) => (
            <div key={i} className="up-row">
              <span className="grow ellipsis">{f.file.name}</span>
              <span className="muted small">{f.state === 'error' ? <span className="error">{f.error}</span> : f.state === 'done' ? 'Uploaded' : f.state === 'waiting' ? 'Waiting' : `${Math.round((f.done / f.file.size) * 100)}%`}</span>
            </div>
          ))}
          <div className="bar"><div style={{ width: `${total ? (done / total) * 100 : 0}%` }} /></div>
          {running && eta && <div className="muted small">{eta}</div>}
        </div>
      )}
      <div className="form-actions">
        {running && <button className="btn btn-ghost" onClick={() => abort.current?.abort()}>Stop</button>}
        {allDone ? (
          <button className="btn btn-primary" onClick={onDone}>Done, show progress</button>
        ) : (
          <button className="btn btn-primary" disabled={!files.length || running} onClick={start}>{running ? `Uploading ${gb(done)} of ${gb(total)}` : 'Upload'}</button>
        )}
      </div>
    </div>
  )
}
